import React, { useMemo, useState } from 'react';
import { FileSpreadsheet, Upload, Link2, X, Plus } from 'lucide-react';
import { BrioButton, BrioSelect } from '../../components/BrioComponents';
import { sumMeasureForLine } from '../hooks/useBoqMeasurements';
import { effectiveUnit } from '../utils/measureUnits';
import { formatQty } from '../utils/money';
import MeasurementTable from './MeasurementTable';
import { ToolbarIcon, ToolbarDivider } from './ToolbarIcon';

const BASIS_OPTIONS = [
  { value: '',                label: '— Manual (no take-off) —' },
  { value: 'count',           label: 'Count (Nr)' },
  { value: 'running_m',       label: 'Running metre' },
  { value: 'plan_area',       label: 'Area — plan (m²)' },
  { value: 'elevation_area',  label: 'Area — elevation (m²)' },
  { value: 'volume',          label: 'Volume (m³)' },
];

function flattenZones(tree, depth = 0, acc = []) {
  tree.forEach((z) => {
    acc.push({ id: z.id, code: z.code, label: `${'— '.repeat(depth)}${z.code} · ${z.label}` });
    if (z.children?.length) flattenZones(z.children, depth + 1, acc);
  });
  return acc;
}

const fmt = formatQty;

function MeasurementSheet({
  line, zoneTree, measurements,
  onBasisChange, onCreate, onUpdate, onRemoveRow,
  onCreateRows, onDeleteMany, onApplyPaste, onToggleDeduct,
  onLinkExisting, onExport, onImport, onClose,
}) {
  const zoneOptions = useMemo(() => flattenZones(zoneTree || []), [zoneTree]);
  const [adding, setAdding] = useState(false);

  const basis = line.measureBasis || '';
  const linked = useMemo(
    () => measurements.filter((m) => m.itemIds.includes(line.id)),
    [measurements, line.id]
  );
  const total = basis ? sumMeasureForLine(linked, basis, line.id) : null;

  const handleAdd = async () => {
    setAdding(true);
    await onCreate(line.id, { nr: 1 });
    setAdding(false);
  };

  return (
    <div style={{ border: '1px solid var(--brio-gold, #9F902A)', borderRadius: 6, padding: '0.75rem 1rem', marginTop: '0.75rem', background: '#fffdf5' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        {/* Left: title + the Bills-on picker. The picker lives here (rather than
            on the right with the toolbar icons) so it's the first thing you see
            — without it set, the take-off total never feeds the line quantity. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.95rem' }}>
            <strong>Take-off</strong> — <code>{line.version?.fullCode || '—'}</code> {line.descriptionOverride || line.version?.shortDescription || ''}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'nowrap' }}>
            <label style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', whiteSpace: 'nowrap' }}>Bills on</label>
            <BrioSelect value={basis} onChange={(e) => onBasisChange(line.id, e.target.value)} style={{ fontSize: '0.85rem' }}>
              {BASIS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </BrioSelect>
          </div>
        </div>
        {/* Right: sheet actions only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
          <ToolbarIcon onClick={() => onExport(line)} tooltip="Export Excel (.xlsx)">
            <FileSpreadsheet size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={() => onImport(line)} tooltip="Import Excel">
            <Upload size={15} />
          </ToolbarIcon>
          <ToolbarIcon onClick={() => onLinkExisting(line)} tooltip="Link existing measurements">
            <Link2 size={15} />
          </ToolbarIcon>
          <ToolbarDivider />
          <ToolbarIcon onClick={onClose} tooltip="Close take-off">
            <X size={15} />
          </ToolbarIcon>
        </div>
      </div>

      {!basis && (
        <div style={{ fontSize: '0.82rem', color: '#a94442', marginBottom: '0.5rem' }}>
          Pick what this line bills on (above) so the take-off total can feed its quantity.
        </div>
      )}

      <MeasurementTable
        rows={linked}
        zoneOptions={zoneOptions}
        basis={basis}
        lineId={line.id}
        defaultItemIds={[line.id]}
        onCommit={onUpdate}
        onDelete={onRemoveRow}
        onCreateRows={onCreateRows}
        onDeleteMany={onDeleteMany}
        onApplyPaste={onApplyPaste}
        onToggleDeduct={onToggleDeduct}
        emptyHint="No measurements yet. Add a row to start taking off."
      />

      {basis && linked.length > 0 && (
        <div style={{ textAlign: 'right', fontWeight: 600, padding: '0.5rem 0.3rem 0', borderTop: '2px solid var(--brio-border, #ddd)', marginTop: '0.25rem' }}>
          Total ({BASIS_OPTIONS.find((o) => o.value === basis)?.label}) → line quantity: {fmt(total)} {effectiveUnit(line)}
        </div>
      )}

      <div style={{ marginTop: '0.5rem' }}>
        <BrioButton variant="primary" onClick={handleAdd} disabled={adding}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
          <Plus size={14} /> {adding ? 'Adding…' : 'Add measurement'}
        </BrioButton>
        <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>
          Highlighted column is what this line bills on. Negative Nr deducts (e.g. openings).
        </span>
      </div>
    </div>
  );
}

export default MeasurementSheet;
